<template>
  <div class="page-container">
    <div class="yn-biz-header">
      <span class="yn-biz-title">土壤检测结果录入</span>
      <div class="header-actions">
        <el-select v-model="curXmbh" size="mini" placeholder="选择任务" filterable clearable @change="onTaskChange" style="width:320px">
          <el-option v-for="t in tasks" :key="t.xmbh" :label="t.xmbh+' - '+t.xmmc" :value="t.xmbh">
            <span>{{ t.xmbh }}</span>
            <span style="color:#8492a6;font-size:12px;margin-left:8px">{{ t.xmmc }}（{{ t.sample_count }}个样品）</span>
          </el-option>
        </el-select>
      </div>
    </div>

    <el-alert
      v-if="curXmbh && labEntryBlocked"
      type="warning"
      show-icon
      :closable="false"
      style="margin-top:16px"
      title="当前项目尚未完成 056 样品交接，暂不可录入理化数据"
    >
      <template slot="default">
        <span>项目阶段：{{ projectPhase && projectPhase.xmzt_label ? projectPhase.xmzt_label : '待交接' }}。056 交样、接样均签字后将自动进入理化队列。</span>
        <el-button type="text" @click="$router.push('/soil/handover?xmbh=' + curXmbh)">前往样品交接</el-button>
      </template>
    </el-alert>

    <div
      v-if="curXmbh && projectPhase && !labEntryBlocked && projectPhase.xmzt === 'lab_ready' && !projectPhase.arranged"
      class="claim-fallback"
    >
      <el-button type="warning" size="mini" plain :loading="claiming" @click="claimSample">确认领样</el-button>
      <span class="claim-fallback-hint">旧数据未安排分析人时需先领样</span>
    </div>

    <template v-if="curXmbh && !labEntryBlocked">
      <el-card shadow="never" class="method-card cma-card">
        <div slot="header" class="section-header">
          <span>1. CMA 能力项（土壤）</span>
          <span class="method-header-hint">先勾选本次检测涉及的 CMA 参数与标准，再选下方原表</span>
        </div>
        <el-table
          ref="cmaTable"
          v-loading="cmaLoading"
          :data="cmaSoilItems"
          border
          stripe
          size="small"
          max-height="320"
          @selection-change="onCmaSelectionChange"
        >
          <el-table-column type="selection" width="42" :selectable="() => !cmaSaving" />
          <el-table-column prop="param_name" label="检测参数" width="120" show-overflow-tooltip />
          <el-table-column prop="std_name" label="依据标准（方法）" min-width="220" show-overflow-tooltip />
          <el-table-column prop="std_code" label="标准编号" width="130" show-overflow-tooltip />
          <el-table-column prop="scope_limit" label="限制范围" width="120" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.scope_limit || '—' }}</template>
          </el-table-column>
        </el-table>
        <div class="cma-foot">
          <span class="cma-count">已选 {{ selectedCmaIds.length }} 项</span>
          <span v-if="!cmaReady" class="cma-warn">请至少选择一项土壤 CMA 能力</span>
        </div>
      </el-card>

      <el-card shadow="never" class="method-card">
        <div slot="header" class="section-header">
          <span>2. 检测原表（自选）</span>
          <span class="method-header-hint">勾选后在下方下载 Word，填好后上传；须在 CMA 能力范围内</span>
        </div>
        <el-alert v-if="!cmaReady" type="info" show-icon :closable="false" title="请先完成上方 CMA 能力项选择" style="margin-bottom:12px" />
        <el-checkbox-group v-model="selectedMethods" class="method-grid" :disabled="!cmaReady" @change="onMethodsChange">
          <el-row :gutter="12">
            <el-col v-for="f in labForms" :key="f.num" :xs="24" :sm="12" :lg="8">
              <el-checkbox :label="f.num" class="method-check-item">
                <span class="method-check-text">GSYN-{{ f.num }} {{ f.mc }}</span>
              </el-checkbox>
            </el-col>
          </el-row>
        </el-checkbox-group>
      </el-card>

      <el-card v-if="entryReady" shadow="never" class="method-card offline-card" v-loading="statusLoading">
        <div slot="header" class="section-header">
          <span>3. 离线 Word 填报</span>
          <span class="method-header-hint">下载预填表 → 本机填写 → 上传回系统（自动抽字段入库）</span>
        </div>
        <el-table :data="offlineRows" border size="small" empty-text="请先勾选检测原表">
          <el-table-column label="原表" min-width="260" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <span class="offline-form-title">GSYN-{{ row.num }} {{ row.mc }}</span>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="120" align="center">
            <template slot-scope="{ row }">
              <el-tag v-if="!row.hasTpl" type="info" size="mini">无模板</el-tag>
              <el-tag v-else-if="row.exists" type="success" size="mini">已上传</el-tag>
              <el-tag v-else type="warning" size="mini">待下载</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="更新时间" width="160" align="center">
            <template slot-scope="{ row }">
              <span class="offline-mtime">{{ formatMtime(row.mtime) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="280" align="center" fixed="right">
            <template slot-scope="{ row }">
              <el-button
                type="primary"
                size="mini"
                plain
                :disabled="!row.hasTpl"
                :loading="row.downloading"
                @click="downloadDocx(row)"
              >{{ row.exists ? '重新下载' : '下载 Word' }}</el-button>
              <el-button
                type="success"
                size="mini"
                :disabled="!row.hasTpl"
                :loading="row.uploading"
                @click="triggerUpload(row)"
              >上传</el-button>
            </template>
          </el-table-column>
        </el-table>
        <input
          ref="docxFileInput"
          type="file"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          class="hidden-file-input"
          @change="onDocxFilePicked"
        >
      </el-card>
    </template>

    <el-empty v-else-if="!loading" description="请先选择一个任务" style="margin-top:80px"></el-empty>
  </div>
</template>

<script>
import api from '@/utils/api'
import { isLoggedIn, userParams } from '@/utils/user'
import axios from 'axios'

const API_BASE = process.env.VUE_APP_API_BASE || ''

export default {
  name: 'SoilEntry',
  data() {
    return {
      tasks: [],
      curXmbh: '',
      projectPhase: null,
      labEntryBlocked: false,
      claiming: false,
      loading: false,
      labForms: [],
      selectedMethods: [],
      cmaSoilItems: [],
      cmaLoading: false,
      cmaSaving: false,
      selectedCmaIds: [],
      _cmaSaveTimer: null,
      _methodsSaveTimer: null,
      statusLoading: false,
      docxStatusMap: {},
      rowBusy: {},
      uploadTargetNum: ''
    }
  },
  computed: {
    cmaReady() {
      return (this.selectedCmaIds || []).length > 0
    },
    entryReady() {
      return this.cmaReady && (this.selectedMethods || []).length > 0
    },
    offlineRows() {
      const nums = Array.from(new Set((this.selectedMethods || []).map(String)))
      return nums.map(num => {
        const hit = (this.labForms || []).find(f => String(f.num) === String(num))
        const st = this.docxStatusMap[num] || {}
        const busy = this.rowBusy[num] || {}
        return {
          num,
          mc: (hit && hit.mc) || '',
          hasTpl: st.has_tpl !== undefined ? !!st.has_tpl : true,
          exists: !!st.exists,
          mtime: st.mtime || 0,
          downloading: !!busy.downloading,
          uploading: !!busy.uploading
        }
      })
    }
  },
  mounted() {
    this.loadRegistry()
    this.loadTasks().then(() => {
      const q = this.$route.query || {}
      if (q.xmbh) {
        this.curXmbh = String(q.xmbh)
        this.onTaskChange()
      }
    })
  },
  beforeDestroy() {
    if (this._methodsSaveTimer) clearTimeout(this._methodsSaveTimer)
    if (this._cmaSaveTimer) clearTimeout(this._cmaSaveTimer)
  },
  watch: {
    entryReady(val) {
      if (val) this.refreshDocxStatus()
    }
  },
  methods: {
    sortLabFormsWithContinuations(forms) {
      const list = Array.isArray(forms) ? forms.slice() : []
      const contMap = {}
      list.forEach(f => {
        const parent = String(f.continuationOf || '')
        if (!parent) return
        if (!contMap[parent]) contMap[parent] = []
        contMap[parent].push(f)
      })
      const out = []
      const used = new Set()
      list.forEach(f => {
        const num = String(f.num)
        if (used.has(num) || f.continuationOf) return
        out.push(f)
        used.add(num)
        ;(contMap[num] || []).forEach(x => {
          out.push(x)
          used.add(String(x.num))
        })
      })
      list.forEach(f => {
        const num = String(f.num)
        if (!used.has(num)) out.push(f)
      })
      return out
    },
    formatMtime(ts) {
      const n = Number(ts) || 0
      if (!n) return '—'
      const d = new Date(n * 1000)
      const p = v => (v < 10 ? '0' + v : '' + v)
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
    },
    setRowBusy(num, key, val) {
      const cur = { ...(this.rowBusy[num] || {}) }
      cur[key] = val
      this.$set(this.rowBusy, String(num), cur)
    },
    async loadRegistry() {
      try {
        const res = await api.get('yn_tr_lab_registry')
        if (res.data && Array.isArray(res.data.labForms)) {
          this.labForms = this.sortLabFormsWithContinuations(res.data.labForms)
        }
      } catch (e) {
        this.$message.error('加载检测方法配置失败')
      }
    },
    async loadTasks() {
      if (!isLoggedIn()) {
        this.$message.warning('登录已失效，请重新登录')
        this.$router.push('/login')
        return
      }
      try {
        const res = await api.get('yn_tr_tasks')
        if (Array.isArray(res.data)) {
          this.tasks = res.data
          if (!this.tasks.length) {
            this.$message.info('暂无检测任务（需先有 App 采样或任务派遣数据）')
          }
        }
      } catch (e) {
        this.$message.error('加载任务列表失败，请刷新或重新登录')
      }
    },
    async onTaskChange() {
      if (!this.curXmbh) {
        this.labEntryBlocked = false
        this.projectPhase = null
        this.selectedCmaIds = []
        this.cmaSoilItems = []
        this.selectedMethods = []
        this.docxStatusMap = {}
        return
      }
      this.loading = true
      await this.loadProjectPhase()
      if (this.labEntryBlocked) {
        this.loading = false
        return
      }
      await Promise.all([this.loadCmaCatalog(), this.loadProjectCma()])
      await this.loadMethods()
      if (this.entryReady) await this.refreshDocxStatus()
      this.loading = false
    },
    canLabEntry(xmzt) {
      const order = { new: 0, pending: 0, dispatched: 1, sampling: 2, handover: 3, lab_ready: 4, labing: 5, doing: 5, lab_done: 6, reporting: 7, done: 8 }
      return (order[String(xmzt)] || 0) >= 4
    },
    async loadProjectPhase() {
      try {
        const res = await api.get('yn_tr_project_phase', { xmbh: this.curXmbh })
        this.projectPhase = res.data && !res.data.error ? res.data : null
        this.labEntryBlocked = !this.canLabEntry(this.projectPhase && this.projectPhase.xmzt)
      } catch (e) {
        this.projectPhase = null
        this.labEntryBlocked = true
      }
    },
    async claimSample() {
      if (!this.curXmbh) return
      this.claiming = true
      try {
        const res = await api.post('yn_tr_lab_claim', { xmbh: this.curXmbh })
        if (res.data && res.data.error) {
          this.$message.error(res.data.error)
          return
        }
        this.$message.success('已领样')
        await this.loadProjectPhase()
      } catch (e) {
        this.$message.error('领样失败')
      } finally {
        this.claiming = false
      }
    },
    async loadProjectCma() {
      try {
        const res = await api.get('yn_tr_project_cma', { xmbh: this.curXmbh })
        const d = res.data || {}
        this.selectedCmaIds = (d.ids || []).map(String)
        this.$nextTick(() => this.syncCmaTableSelection())
      } catch (e) {
        this.selectedCmaIds = []
      }
    },
    async loadCmaCatalog() {
      this.cmaLoading = true
      try {
        const res = await api.get('yn_cma_scope_list', {
          major: '三、土壤和水系沉积物',
          page: 1,
          page_size: 200
        })
        this.cmaSoilItems = (res.data && res.data.items) || []
        this.$nextTick(() => this.syncCmaTableSelection())
      } catch (e) {
        this.cmaSoilItems = []
        this.$message.error('加载土壤 CMA 能力项失败')
      } finally {
        this.cmaLoading = false
      }
    },
    syncCmaTableSelection() {
      const table = this.$refs.cmaTable
      if (!table) return
      table.clearSelection()
      const idSet = new Set((this.selectedCmaIds || []).map(String))
      this.cmaSoilItems.forEach(row => {
        if (idSet.has(String(row.id))) table.toggleRowSelection(row, true)
      })
    },
    onCmaSelectionChange(rows) {
      this.selectedCmaIds = (rows || []).map(r => String(r.id))
      if (this._cmaSaveTimer) clearTimeout(this._cmaSaveTimer)
      this._cmaSaveTimer = setTimeout(() => {
        if (this.curXmbh) this.saveProjectCma({ silent: true })
      }, 500)
    },
    async saveProjectCma(opts = {}) {
      const silent = !!opts.silent
      if (!this.curXmbh) return
      this.cmaSaving = true
      try {
        const res = await api.post('yn_tr_project_cma', {
          xmbh: this.curXmbh,
          ids: JSON.stringify(this.selectedCmaIds.map(id => parseInt(id, 10)).filter(n => n > 0))
        })
        const d = res.data || {}
        if (d.error) {
          if (!silent) this.$message.error(d.error)
          return
        }
        this.selectedCmaIds = (d.ids || []).map(String)
        if (!silent) this.$message.success('CMA 能力项已保存')
      } catch (e) {
        if (!silent) this.$message.error('保存 CMA 能力项失败')
      } finally {
        this.cmaSaving = false
      }
    },
    async loadMethods() {
      try {
        const res = await api.get('yn_tr_methods', { xmbh: this.curXmbh })
        const list = (res.data && res.data.methods) || []
        if (this.cmaReady) {
          this.selectedMethods = list.filter(m => m.enabled == 1).map(m => String(m.form_num))
        } else {
          this.selectedMethods = []
        }
      } catch (e) {
        this.selectedMethods = []
      }
    },
    onMethodsChange() {
      if (this._methodsSaveTimer) clearTimeout(this._methodsSaveTimer)
      this._methodsSaveTimer = setTimeout(() => {
        if (this.curXmbh) this.saveMethods({ silent: true })
      }, 600)
      if (this.entryReady) this.refreshDocxStatus()
    },
    async saveMethods(opts = {}) {
      const silent = !!opts.silent
      const methods = Array.from(new Set((this.selectedMethods || []).map(String)))
      try {
        const res = await api.post('yn_tr_methods', {
          xmbh: this.curXmbh,
          methods: JSON.stringify(methods)
        })
        if (res.data && res.data.ok) {
          if (!silent) this.$message.success('检测方法已保存')
          const list = res.data.methods || []
          const got = list.filter(m => m.enabled == 1).map(m => String(m.form_num))
          const merged = got.slice()
          methods.forEach(n => {
            if (/^\d+-\d+$/.test(n) && merged.indexOf(n) < 0) merged.push(n)
          })
          this.selectedMethods = merged
          await this.refreshDocxStatus()
        } else if (!silent) {
          this.$message.error('保存失败')
        }
      } catch (e) {
        if (!silent) this.$message.error('保存失败')
      }
    },
    async refreshDocxStatus() {
      const forms = Array.from(new Set((this.selectedMethods || []).map(String)))
      if (!this.curXmbh || !forms.length) {
        this.docxStatusMap = {}
        return
      }
      this.statusLoading = true
      try {
        const res = await api.get('yn_tr_lab_docx_status', {
          xmbh: this.curXmbh,
          forms: JSON.stringify(forms),
          scope: 'batch'
        })
        const d = res.data || {}
        if (d.error) {
          this.$message.error(d.error)
          return
        }
        const map = {}
        ;(d.items || []).forEach(it => {
          map[String(it.form_num)] = {
            has_tpl: Number(it.has_tpl) === 1,
            exists: Number(it.exists) === 1,
            mtime: Number(it.mtime) || 0,
            size: Number(it.size) || 0
          }
        })
        this.docxStatusMap = map
      } catch (e) {
        this.$message.error('加载原表状态失败')
      } finally {
        this.statusLoading = false
      }
    },
    async downloadDocx(row) {
      if (!row || !row.hasTpl) return
      this.setRowBusy(row.num, 'downloading', true)
      try {
        const params = userParams({
          yn_tr_lab_docx_download: '1',
          xmbh: this.curXmbh,
          form_num: row.num,
          scope: 'batch'
        })
        const res = await axios.get(`${API_BASE}/yn_api.php`, {
          params,
          responseType: 'blob'
        })
        const ct = String((res.headers && res.headers['content-type']) || '')
        if (ct.indexOf('application/json') >= 0 || ct.indexOf('text/') >= 0) {
          let text = ''
          if (res.data && typeof res.data.text === 'function') {
            text = await res.data.text()
          } else if (res.data instanceof Blob) {
            text = await new Promise((resolve, reject) => {
              const fr = new FileReader()
              fr.onload = () => resolve(String(fr.result || ''))
              fr.onerror = reject
              fr.readAsText(res.data)
            })
          }
          let msg = '下载失败'
          try {
            const j = JSON.parse(text)
            if (j && j.error) msg = j.error
          } catch (e) { /* ignore */ }
          this.$message.error(msg)
          return
        }
        const blob = res.data
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `GSYN-${row.num}.docx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
        this.$message.success('已开始下载')
        await this.refreshDocxStatus()
      } catch (e) {
        this.$message.error('下载失败')
      } finally {
        this.setRowBusy(row.num, 'downloading', false)
      }
    },
    triggerUpload(row) {
      if (!row || !row.hasTpl) return
      this.uploadTargetNum = String(row.num)
      const input = this.$refs.docxFileInput
      if (input) {
        input.value = ''
        input.click()
      }
    },
    async onDocxFilePicked(ev) {
      const file = ev && ev.target && ev.target.files && ev.target.files[0]
      const num = this.uploadTargetNum
      this.uploadTargetNum = ''
      if (!file || !num) return
      const name = String(file.name || '').toLowerCase()
      if (!name.endsWith('.docx')) {
        this.$message.warning('请选择 .docx 文件')
        return
      }
      this.setRowBusy(num, 'uploading', true)
      try {
        const res = await api.upload('yn_tr_lab_docx_upload', file, {
          xmbh: this.curXmbh,
          form_num: num,
          scope: 'batch'
        })
        const d = res.data || {}
        if (d.error) {
          this.$message.error(d.error)
          return
        }
        this.$message.success(`GSYN-${num} 已上传并入库`)
        await this.refreshDocxStatus()
      } catch (e) {
        this.$message.error('上传失败')
      } finally {
        this.setRowBusy(num, 'uploading', false)
      }
    }
  }
}
</script>

<style scoped>
.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.method-header-hint {
  color: #909399;
  font-size: 12px;
  font-weight: 400;
}
.method-grid {
  width: 100%;
}
.method-check-item {
  display: flex;
  align-items: flex-start;
  margin: 0 0 14px;
  white-space: normal;
}
.method-check-item >>> .el-checkbox__input {
  margin-top: 3px;
}
.method-check-item >>> .el-checkbox__label {
  display: inline-block;
  white-space: normal;
  line-height: 1.5;
  word-break: break-word;
  padding-left: 8px;
}
.method-check-text {
  display: inline-block;
  max-width: 100%;
}
.method-card {
  margin-top: 16px;
}
.cma-card { margin-top: 16px; }
.cma-foot {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  font-size: 13px;
}
.cma-count { color: #606266; }
.cma-warn { color: #e6a23c; }
.claim-fallback {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}
.claim-fallback-hint {
  font-size: 12px;
  color: #909399;
}
.offline-card {
  margin-bottom: 24px;
}
.offline-form-title {
  font-weight: 500;
  color: #303133;
}
.offline-mtime {
  font-size: 12px;
  color: #909399;
}
.hidden-file-input {
  display: none;
}
</style>
